import { z } from 'zod';
import { optionalE164PhoneSchema, requiredE164PhoneSchema } from './common/phone.js';

// --- Sub-schemas for BCP sub-tabs ---

export const BcpIntroItemSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    content: z.string(),
    author: z.string(),
    date: z.string()
});

export const BcpLocationSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    assetId: z.string().min(1, "form.validation.assetRequired"),
    category: z.string().min(1, "form.validation.categoryRequired"),
    location: z.string().min(1, "form.validation.locationRequired"),
    country: z.string().min(1, "form.validation.countryRequired"),
    address: z.string().min(1, "form.validation.addressRequired"),
    type: z.string().min(1, "form.validation.typeRequired"),
    remarks: z.string().optional(),
});

export const BcpRelatedExercisesSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    exerciseType: z.string().min(1, "form.validation.exerciseTypeRequired"),
    objectives: z.string().optional(),
    exerciseDate: z.string().min(1, "form.validation.dateRequired"),
    duration: z.string().optional(),
    location: z.string().optional(),
    facilitator: z.string().optional(),
    participatingVendors: z.string().optional(),
    daysForExerciseStart: z.union([z.number(), z.string()]).optional(),
    successRate: z.union([z.number(), z.string()]).optional(),
    status: z.string().optional(),
});

export const BcpReportSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    description: z.string().optional(),
    author: z.string().optional(),
    date: z.string().min(1, "form.validation.dateRequired"),
    type: z.string().optional(),
    url: z.string().optional(),
    version: z.string().optional(),
});

export const BcpVendorSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    vendor: z.string().min(1, "form.validation.vendorRequired"),
    contact: z.string().optional(),
    product: z.string().optional(),
    expiration: z.string().optional(),
    address: z.string().optional(),
    businessImpact: z.string().optional(),
    rto: z.string().optional(),
    remarks: z.string().optional(),
});

export const BcpProcessSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    process: z.string().min(1, "form.validation.processRequired"),
    description: z.string().optional(),
    unit: z.string().min(1, "form.validation.unitRequired"),
    rto: z.string().min(1, "form.validation.rtoRequired"),
    rpo: z.string().min(1, "form.validation.rpoRequired"),
    mtpd: z.string().min(1, "form.validation.mtpdRequired"),
    criticality: z.string().min(1, "form.validation.criticalityRequired"),
});

export const BcpApplicationSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    application: z.string().min(1, "form.validation.applicationRequired"),
    description: z.string().optional(),
    rto: z.string().min(1, "form.validation.rtoRequired"),
    vendor: z.string().optional(),
    owner: z.string().optional(),
    version: z.string().optional(),
    remarks: z.string().optional(),
});

export const BcpGenericContactSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    mobile: optionalE164PhoneSchema,
    landline: optionalE164PhoneSchema,
    email: z.string().email("form.validation.invalidEmail").optional().or(z.literal("")),
    position: z.string().optional(),
    unit: z.string().optional(),
    responsible: z.string().optional(),
    remarks: z.string().optional(),
});

export const BcpCombinedContactSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    category: z.string().min(1, "form.validation.categoryRequired"),
    name: z.string().min(1, "form.validation.nameRequired"),
    mobile: requiredE164PhoneSchema,
    landline: optionalE164PhoneSchema,
    email: z.string().email("form.validation.invalidEmail").optional().or(z.literal("")),
    position: z.string().optional(),
    unit: z.string().optional(),
    responsible: z.string().optional(),
    remarks: z.string().optional(),
    contactSubject: z.string().optional(),
    inWhichCase: z.string().optional(),
    who: z.string().optional(),
    withWhom: z.string().optional(),
    whichMethod: z.string().optional(),
});

export const BcpEmergencyContactSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    mobile: requiredE164PhoneSchema,
    remarks: z.string().optional(),
});

export const BcpCommPlanSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    goal: z.string().min(1, "form.validation.goalRequired"),
    audience: z.string().min(1, "form.validation.audienceRequired"),
    messaging: z.string().min(1, "form.validation.messagingRequired"),
    channels: z.string().min(1, "form.validation.channelsRequired"),
    frequency: z.string().min(1, "form.validation.frequencyRequired"),
    owner: z.string().min(1, "form.validation.ownerRequired"),
    ownerId: z.string().optional(),
    assignmentsByRole: z.record(z.array(z.any())).optional(),
    customFields: z.record(z.unknown()).optional(),
});

export const BcpInitialResponseSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    step: z.string().min(1, "form.validation.stepRequired"),
    action: z.string().min(1, "form.validation.actionRequired"),
    responsible: z.string().min(1, "form.validation.responsibleRequired"),
    details: z.string().optional(),
    timeframe: z.string().min(1, "form.validation.timeframeRequired"),
    include: z.boolean().default(true),
});

// --- Main BCP Schema ---

export const BcpSchema = z.object({
    id: z.union([z.string(), z.number()]).optional(),
    _id: z.string().optional(),
    tenantId: z.string().optional(),
    name: z.string().min(1, "form.validation.nameRequired"),
    description: z.string().nullish(),
    bias: z.any().nullish(),
    process: z.any().nullish(),
    unit: z.any().nullish(),
    unitId: z.string().nullish(),
    location: z.any().nullish(),

    nextReview: z.union([z.string(), z.date()]).nullish(),
    daysForNextReview: z.union([z.number(), z.string()]).nullish(),
    isPeriodic: z.boolean().or(z.string().transform(v => v === 'true')).default(false).nullish(),
    reviewPeriod: z.union([z.number(), z.string()]).nullish(),
    reviewUnit: z.string().nullish(),

    activationCriteria: z.string().nullish(),
    activationProcedure: z.string().nullish(),
    deactivationProcedure: z.string().nullish(),
    phases: z.any().nullish(),
    rolesData: z.any().nullish(),
    communicationPlan: z.any().nullish(),
    alternateLocation: z.any().nullish(),
    requiredResources: z.any().nullish(),
    documents: z.any().nullish(),
    inScope: z.any().nullish(),
    processes: z.any().nullish(),
    assets: z.any().nullish(),
    vendors: z.any().nullish(),
    introItems: z.any().nullish(),
    initialResponse: z.any().nullish(),
    initialResponseComments: z.any().nullish(),
    communications: z.any().nullish(),
    locations: z.any().nullish(),
    relatedExercises: z.any().nullish(),
    reports: z.any().nullish(),
    lastExerciseAt: z.union([z.string(), z.date()]).nullish(),
    lastExerciseScore: z.number().nullish(),
    lastExerciseSummary: z.string().nullish(),
    lastExerciseId: z.string().nullish(),
    partyInstanceId: z.string().nullish(),

    state: z.string().nullish(),
    status: z.string().nullish(),
    customFields: z.record(z.unknown()).optional(),

    createdAt: z.union([z.date(), z.string()]).optional(),
    updatedAt: z.union([z.date(), z.string()]).optional(),
});

export const CreateBcpSchema = BcpSchema.omit({ id: true, _id: true, createdAt: true, updatedAt: true });
export const UpdateBcpSchema = BcpSchema.partial();

// Frontend Form Schema
export const BcpFormSchema = z.object({
    name: z.string().min(1, "form.validation.nameRequired"),
    description: z.string().nullish(),
    unit: z.string().nullish(),
    unitId: z.string().nullish(),
    location: z.string().nullish(),
    nextReview: z.string().nullish(),
    daysForNextReview: z.union([z.number(), z.string()]).nullish(),
    isPeriodic: z.boolean().default(false),
    reviewPeriod: z.union([z.number(), z.string()]).nullish(),
    reviewUnit: z.string().nullish(),
    state: z.string().nullish(),
    activationCriteria: z.string().nullish(),
    activationProcedure: z.string().nullish(),
    deactivationProcedure: z.string().nullish(),
    phases: z.any().nullish(),
});

export const CommunicationPlanSchema = BcpCommPlanSchema;
export const CreateCommunicationPlanSchema = BcpCommPlanSchema.omit({ id: true });
export const UpdateCommunicationPlanSchema = BcpCommPlanSchema.partial();
